import { createClient, Session } from '@supabase/supabase-js';
import { Database } from '../types/database';
import { projectId, publicAnonKey } from '../utils/supabase/info';

const supabaseUrl = `https://${projectId}.supabase.co`;

// Markly Chrome Extension ID (update after publishing)
const EXTENSION_ID = import.meta.env.VITE_EXTENSION_ID;

// Create singleton Supabase client
export const supabase = createClient<Database>(supabaseUrl, publicAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
});

// Sync session with Chrome extension
export async function syncSessionWithExtension(session: Session | null): Promise<boolean> {
  if (!EXTENSION_ID) return false;

  try {
    // Check if chrome.runtime is available (only in Chrome browser with extension installed)
    if (typeof chrome !== 'undefined' && chrome.runtime?.sendMessage) {
      await chrome.runtime.sendMessage(EXTENSION_ID, {
        type: 'SYNC_SESSION',
        session,
      });
      console.log('Session synced with extension');
      return true;
    }
  } catch (error) {
    // Extension not installed or not reachable - ignore silently
    console.debug('Extension sync skipped:', error);
  }
  return false;
}

// Response type from extension
interface ExtensionSessionResponse {
  session?: Session | null;
}

// Request session from Chrome extension (when user logged in via extension first)
async function requestSessionFromExtension(): Promise<Session | null> {
  if (!EXTENSION_ID) return null;

  try {
    if (typeof chrome !== 'undefined' && chrome.runtime?.sendMessage) {
      const response = await chrome.runtime.sendMessage(EXTENSION_ID, {
        type: 'REQUEST_SESSION',
      }) as ExtensionSessionResponse;

      if (response?.session) {
        // Set the session in web app's Supabase client
        const { data, error } = await supabase.auth.setSession({
          access_token: response.session.access_token,
          refresh_token: response.session.refresh_token,
        });

        if (!error && data.session) {
          console.log('Session restored from extension');
          return data.session;
        }
      }
    }
  } catch (error) {
    // Extension not installed or not reachable - ignore silently
    console.debug('Extension session request skipped:', error);
  }

  return null;
}

// Logout from both web app and extension
export async function logoutAll(): Promise<void> {
  // Logout from Supabase (web app)
  await supabase.auth.signOut();

  // Notify extension to logout as well
  if (EXTENSION_ID && typeof chrome !== 'undefined' && chrome.runtime?.sendMessage) {
    try {
      await chrome.runtime.sendMessage(EXTENSION_ID, {
        type: 'LOGOUT_ALL',
      });
      console.log('Logout synced with extension');
    } catch {
      // Extension not available, ignore
    }
  }
}

// Listen for auth state changes and sync with extension
supabase.auth.onAuthStateChange(async (event, session) => {
  if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
    await syncSessionWithExtension(session);
  } else if (event === 'SIGNED_OUT') {
    await syncSessionWithExtension(null);
  }
});

// Initial sync on page load
(async () => {
  const { data: { session } } = await supabase.auth.getSession();
  if (session) {
    // Already have session, sync to extension
    await syncSessionWithExtension(session);
  } else {
    // No local session - try to get from extension
    await requestSessionFromExtension();
  }
})();

// Helper to get current user
export const getCurrentUser = async () => {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error) throw error;
  return user;
};

// Helper to get session
export const getSession = async () => {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) throw error;
  return session;
};
